<template>
  <v-dialog
    v-model="dialog"
    scrollable
    persistent
    content-class="align-self-start"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="primary"
        text
        v-on="on"
      >
        <v-icon left>
          cloud_download
        </v-icon>
        Import
      </v-btn>
    </template>

    <v-card
      max-height="calc(100vh - 95px)"
    >
      <v-card-title>
        Import {{ model }} {{ project ? 'into project' : null }}

        <v-stepper
          v-model="currentStep"
          class="elevation-0 body-2"
          style="width: 100%;"
        >
          <v-stepper-header class="elevation-0">
            <v-stepper-step
              :complete="currentStep > 1"
              step="1"
            >
              Upload a file
            </v-stepper-step>

            <v-divider />

            <v-stepper-step
              :complete="currentStep > 2"
              step="2"
            >
              Match columns
            </v-stepper-step>

            <v-divider />

            <v-stepper-step
              :complete="currentStep > 3"
              step="3"
            >
              Review Data
            </v-stepper-step>

            <v-divider />

            <v-stepper-step
              :complete="currentStep > 4"
              step="4"
            >
              Complete
            </v-stepper-step>
          </v-stepper-header>
        </v-stepper>
      </v-card-title>

      <v-divider />

      <v-card-text ref="cardText">
        <v-stepper
          v-model="currentStep"
          class="elevation-0"
        >
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-file-input
                ref="fileInput"
                v-model="inputFile"
                label="Select a file..."
                outlined
                accept=".csv,text/csv,.json,application/json"
                hint="Accepted formats: CSV, JSON"
                persistent-hint
                :loading="parsing"
                @change="onFileChange($event)"
              />
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-row>
                <v-col cols="5">
                  <div class="text-center">
                    Your Data ({{ inputFile ? inputFile.name : 'No file' }})
                  </div>
                </v-col>

                <v-col
                  cols="2"
                  align-self="center"
                  class="text-center"
                >
                  <v-icon>arrow_right_alt</v-icon>
                </v-col>

                <v-col cols="5">
                  <div class="text-center">
                    Atlas {{ model }}
                  </div>
                </v-col>
              </v-row>

              <v-row
                v-for="property in rawDataKeys"
                :key="`prop-${property}`"
              >
                <v-col cols="6">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-center">
                            {{ property }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, i) in rawData.slice(0, 3)"
                          :key="`preview-item-${i}`"
                        >
                          <td>{{ item[property] }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>

                <v-col cols="6">
                  <v-select
                    v-model="importMap[property]"
                    :items="tableHeaders"
                    label="Select a Field"
                    :item-disabled="checkIfFieldAssigned(property)"
                    outlined
                    clearable
                    single-line
                    hide-details
                  />

                  <div v-if="importMap[property]">
                    <v-subheader>
                      <v-icon
                        small
                        color="success"
                      >
                        check_circle
                      </v-icon>
                      The column {{ property }} will be imported as {{ importMap[property] }}
                    </v-subheader>
                  </div>

                  <div v-else>
                    <v-subheader>
                      <v-icon
                        small
                        color="warning"
                      >
                        warning
                      </v-icon>
                      The column {{ property }} will not be imported
                    </v-subheader>
                  </div>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content step="3">
              <div class="text-center mb-4 subtitle">
                Here all the {{ model }} that will be imported,
                please review them before importing.
              </div>

              <v-data-table
                :headers="tableHeaders"
                :items="toImport"
                :footer-props="{
                  'items-per-page-options': [25,50,100,-1]
                }"
              >
                <template v-slot:items="props">
                  <td
                    v-for="header in tableHeaders"
                    :key="`item-${header.value}`"
                  >
                    {{ props.item[header.value] || '-' }}
                  </td>
                </template>
              </v-data-table>
            </v-stepper-content>

            <v-stepper-content step="4">
              <div class="text-center display-2">
                Congratulations!
              </div>

              <div class="text-center title my-4">
                You successfully imported {{ importedCount }} {{ model }}.
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>

      <v-divider />

      <v-card-actions v-if="currentStep === 1">
        <v-btn
          text
          @click="abortImport()"
        >
          Cancel
        </v-btn>

        <v-spacer />
      </v-card-actions>

      <v-card-actions v-else-if="currentStep === 2">
        <v-btn
          text
          color="error"
          @click="abortImport()"
        >
          Abort
        </v-btn>

        <v-spacer />

        <v-btn
          color="primary"
          text
          @click="prepareDataToImport()"
        >
          Continue
          <v-icon right>
            keyboard_arrow_right
          </v-icon>
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else-if="currentStep === 3">
        <v-btn
          color="error"
          text
          :disabled="importing"
          @click="abortImport()"
        >
          Abort
        </v-btn>

        <v-spacer />

        <v-btn
          text
          :disabled="importing"
          @click="goToPreviousStep()"
        >
          <v-icon left>
            keyboard_arrow_left
          </v-icon>
          Back
        </v-btn>

        <v-btn
          color="primary"
          text
          :loading="importing"
          :disabled="importing"
          @click="doImport()"
        >
          <v-icon left>
            cloud_download
          </v-icon>
          Import {{ toImport.length }} entries
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else-if="currentStep === 4">
        <v-spacer />

        <v-btn
          text
          @click="abortImport()"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Papa from 'papaparse'

import { db } from '@/plugins/stitch'
import Activity from '@/models/Activity'
import Expense from '@/models/Expense'
import Project from '@/models/Project'
import Resource from '@/models/Resource'

const ALL_MODELS = [
  { text: 'Activities', value: 'activities', model: Activity },
  { text: 'Expenses', value: 'expenses', model: Expense },
  { text: 'Projects', value: 'projects', model: Project },
  { text: 'Resources', value: 'resources', model: Resource }
]

export default {
  props: {
    model: {
      type: String,
      required: true,
      validator: (val) => {
        return ALL_MODELS.map(m => m.value).includes(val)
      }
    },
    project: {
      type: String,
      required: false,
      default: null
    }
  },
  data () {
    return {
      currentStep: 1,
      dialog: false,
      // keys mapping from input file to atlas model
      importMap: {},
      importedCount: 0,
      importing: false,
      inputFile: null,
      parsing: false,
      // rawData is the input file content
      rawData: [],
      rawDataKeys: [],
      // all properties kes of atlas model
      tableHeaders: [],
      toImport: []
    }
  },
  mounted () {
    const Klass = ALL_MODELS.find(m => m.value === this.model).model

    const attributes = new Klass().attributes
    delete attributes.id
    delete attributes.organization

    this.tableHeaders = Object.keys(attributes).map(key => {
      return { text: key, value: key }
    })
  },
  methods: {
    // Handle file input change and parse file
    onFileChange (file) {
      if (!file) { return }
      let extension = file.name.split('.')
      extension = extension[extension.length - 1]

      this.parsing = true

      if (file.type === 'text/csv' ||
          file.type === 'text/comma-separated-values' ||
          (file.type === 'application/vnd.ms-excel' && extension === 'csv')) {
        // CSV
        Papa.parse(file, {
          skipEmptyLines: true,
          header: true,
          complete: results => this.handleFileContent(results.data),
          error: (err, file, inputElem, reason) => {
            this.$notifyError('Failed to parse CSV file', err)
            throw err
          }
        })
      } else if (file.type === 'application/json') {
        // JSON
        let reader = new FileReader()

        reader.onloadend = event => {
          this.handleFileContent(JSON.parse(event.target.result))
        }
        reader.readAsText(file)
      } else {
        this.$notifyError('Unsupported file type ' + file.type)
      }
    },
    handleFileContent (content) {
      this.parsing = false
      this.rawData = content
      this.rawDataKeys = Object.keys(content[0])

      this.importMap = {}

      for (const key of this.rawDataKeys) {
        const matchingKey = this.tableHeaders
          .find(h => h.value.toLowerCase().replace(/\s+/, '') === key.toLowerCase().replace(/\s+/, ''))

        if (matchingKey) {
          this.importMap[key] = matchingKey.value
        }
      }

      // after file upload go to next step
      this.goToNextStep()
    },
    getEntryProperty (entry, key) {
      if (this.importMap[key]) {
        return entry[this.importMap[key]]
      } else {
        return undefined
      }
    },
    checkIfFieldAssigned (property) {
      return ({ value }) => {
        for (const key in this.importMap) {
          if (value === this.importMap[key] && property !== key) {
            return true
          }
        }
      }
    },
    prepareDataToImport () {
      this.toImport = []
      const now = new Date().getTime()

      const defaultProps = {
        organization: this.$currentOrganization.id,
        createdBy: this.$currentUser.id,
        createdAt: now,
        updatedAt: now
      }

      // Eventually add project if the import is in project scope
      if (this.project) {
        defaultProps.project = this.project
      }

      let toWrite
      for (let entry of this.rawData) {
        toWrite = {}

        for (const key of this.rawDataKeys) {
          // value = this.getEntryProperty(entry, key)
          if (this.importMap[key]) {
            toWrite[this.importMap[key]] = entry[key]
          }
        }

        Object.assign(toWrite, defaultProps)

        this.toImport.push(toWrite)
      }

      this.goToNextStep()
    },
    async doImport () {
      this.importing = true

      try {
        const { insertedIds } = await db.collection(this.model).insertMany(this.toImport)
        this.$notify('Successfully imported data')
        this.goToNextStep()

        this.importedCount = Object.values(insertedIds).length
      } catch (err) {
        this.$notifyError('Failed to import data', err)
      } finally {
        this.importing = false
      }
    },
    goToNextStep () {
      this.currentStep += 1
      this.$refs.cardText.scrollTop = 0
    },
    goToPreviousStep () {
      this.currentStep -= 1
      this.$refs.cardText.scrollTop = 0
    },
    abortImport () {
      this.currentStep = 1
      this.importMap = {}
      this.inputFile = null
      // Workaround to clear input value and re-upload same file
      this.$refs.fileInput.$el.querySelector('input').value = null
      this.rawData = []
      this.rawDataKeys = []
      this.toImport = []

      this.dialog = false
    }
  }
}
</script>

<style>
.import__items {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
